import React, { FC, useCallback, useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import Bookmarks from "@material-ui/icons/Bookmarks";
import Class from "@material-ui/icons/Class";
import School from "@material-ui/icons/School";
import Receipt from "@material-ui/icons/Receipt";
import Work from "@material-ui/icons/Work";
import Category from "@material-ui/icons/Category";
import PeopleAlt from "@material-ui/icons/PeopleAlt";
import makeStyles from "@material-ui/core/styles/makeStyles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { Collapse, createStyles, Theme } from "@material-ui/core";
import AccessControl from "../../../../components/AccessControl";
import FontIcon from "../../../../components/FontIcon";
import { AdapterLink } from "../../../../components/Adopter";
import { Routes } from "../../../../constants/routes";
import { useApp } from "../../hooks/useApp";
import { UserRole } from "../../../user/enums/UserRole";
import { useAuth } from "../../../auth/hooks/useAuth";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {},
    nested: {
      paddingLeft: theme.spacing(2),
    },
    fullList: {
      width: "auto",
    },
  }),
);

const AdminDrawerContent = React.memo((props) => {
  const classes = useStyles();

  const { user } = useAuth();

  const {
    actions: { toggleAdminDrawer },
  } = useApp();

  const isAdmin = [UserRole.Owner, UserRole.Admin].includes(user?.role);

  return (
    <List component="nav" className={classes.list}>
      {isAdmin && (
        <>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/degree"}
          >
            <ListItemIcon>
              <School />
            </ListItemIcon>
            <ListItemText primary={"Degrees"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/exam"}
          >
            <ListItemIcon>
              <Class />
            </ListItemIcon>
            <ListItemText primary={"Exams"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/major"}
          >
            <ListItemIcon>
              <Bookmarks />
            </ListItemIcon>
            <ListItemText primary={"Majors"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/school"}
          >
            <ListItemIcon>
              <FontIcon name="school" />
            </ListItemIcon>
            <ListItemText primary={"Schools"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/academics"}
          >
            <ListItemIcon>
              <Work />
            </ListItemIcon>
            <ListItemText primary={"Academics"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/application"}
          >
            <ListItemIcon>
              <Receipt />
            </ListItemIcon>
            <ListItemText primary={"Applications"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={"/0/services"}
          >
            <ListItemIcon>
              <Category />
            </ListItemIcon>
            <ListItemText primary={"Services"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={`/0/${Routes.STATISTICS}`}
          >
            <ListItemIcon>
              <FontIcon name="statistics" />
            </ListItemIcon>
            <ListItemText primary={"Statistics"} />
          </ListItem>
          <ListItem
            onClick={toggleAdminDrawer}
            button
            component={AdapterLink}
            to={`/0/${Routes.BROADCAST}`}
          >
            <ListItemIcon>
              <FontIcon name="bullhorn" />
            </ListItemIcon>
            <ListItemText primary={"Broadcast"} />
          </ListItem>
        </>
      )}

      <AdminUserCollapseContent onClose={toggleAdminDrawer} />
    </List>
  );
});

const AdminUserCollapseContent: FC<{ onClose: Function }> = React.memo(
  ({ onClose }) => {
    const classes = useStyles();
    const { path } = useRouteMatch();

    const nestedItems = useMemo(
      () => [
        {
          roles: [UserRole.Owner],
          path: `/0/${Routes.ADMINS}`,
          label: "Admins",
          icon: <PeopleAlt />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin, UserRole.Support],
          path: `/0/${Routes.SCHOOL_LIST}`,
          label: "Schools",
          icon: <FontIcon name={"school"} fontSize={"small"} />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin],
          path: `/0/${Routes.SERVICE_PROVIDER_LIST}`,
          label: "Service providers",
          icon: <Category />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin],
          path: `/0/${Routes.TEACHERS}`,
          label: "Teachers",
          icon: <School />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin],
          path: `/0/${Routes.AGENTS}`,
          label: "Agents",
          icon: <FontIcon name={"agent"} fontSize={"small"} />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin],
          path: `/0/${Routes.PARENTS}`,
          label: "Parents",
          icon: <FontIcon name={"service-guardianship"} fontSize={"small"} />,
        },
        {
          roles: [UserRole.Owner, UserRole.Admin],
          path: `/0/${Routes.USER_STUDENTS}`,
          label: "Students",
          icon: <FontIcon name={"student"} fontSize={"small"} />,
        },
      ],
      [Routes],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const [open, setOpen] = useState(path.includes("/0/user"));
    const toggleUsersCollapse = useCallback(() => {
      setOpen((prevState) => !prevState);
    }, [setOpen]);

    return (
      <>
        <ListItem onClick={toggleUsersCollapse} button>
          <ListItemIcon>
            <PeopleAlt />
          </ListItemIcon>
          <ListItemText primary={"Users"} />
        </ListItem>
        <Collapse in={open}>
          <List className={classes.nested}>
            {nestedItems.map((item) => (
              <AccessControl allowedRoles={item.roles} key={item.path}>
                <ListItem
                  onClick={onClose}
                  button
                  component={AdapterLink}
                  to={item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              </AccessControl>
            ))}
          </List>
        </Collapse>
      </>
    );
  },
);

export default AdminDrawerContent;
